'use client';

import React, { useEffect, useLayoutEffect, useState } from 'react';
import Markdown from 'react-markdown';
import { useConfig } from '@/app/contexts/ConfigContext';
import { LanguageOptions } from '@/types';
import Loader from '@/components/Loader';
import TermsSpecificToCH from '@/components/TermsSpecificToCH';
import TermsSpecificToCY from '@/components/TermsSpecificToCY';
import TermsSpecificToNO from '@/components/TermsSpecificToNO';
import { COMPANY_COUNTRY_WORDING } from '@/configs/companies';
import { scrollPageToTop } from '@/helpers';

export default function Terms() {
  const { config, selectedLanguage, wording } = useConfig(); // Retrieve the selected language from the context
  const [markdownContent, setMarkdownContent] = useState('');
  const [hasLangLoaded, setHasLangLoaded] = useState(false);

  useLayoutEffect(() => {
    scrollPageToTop();
  }, []);

  useEffect(() => {
    if (markdownContent) {
      scrollPageToTop();
    }
  }, [markdownContent]);

  // Used to delay the md loading until the language is loaded
  useEffect(() => {
    if (selectedLanguage) {
      setHasLangLoaded(true);
    }
  }, [selectedLanguage]);

  // Load the Markdown file based on the config language
  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        const legalParams = new URLSearchParams({
          serviceUrlLink: config.SERVICE_URL_LINK,
          language: selectedLanguage,
          fileName: 'terms',
        });

        if (config.FLOW_TYPE) {
          legalParams.set('flowType', config.FLOW_TYPE);
        }

        if (config.LEGALS_DYNAMIC_S3_PATH_FROM_SERVICE_URL) {
          legalParams.set('legalsDynamicS3PathFromServiceUrl', 'true');
        }

        const response = await fetch(
          config.TERMS_URL
            ? `api/legals-custom-url?url=${config.TERMS_URL}`
            : `api/legals?${legalParams.toString()}`,
        );
        if (!response.ok) {
          throw new Error(`[Terms] Could not load file: terms.md`);
        }

        const { fileContents } = await response.json();

        const handleServiceCountry = () => {
          const serviceCountry = config.SERVICE_COUNTRY;

          // Only one language
          if (typeof serviceCountry === 'string') {
            return serviceCountry;
          }

          // Multiple languages
          if (selectedLanguage && serviceCountry[selectedLanguage]) {
            return serviceCountry[selectedLanguage];
          }
          // SelectedLanguage not available
          const firstKey = Object.keys(serviceCountry)[0];
          return firstKey ? serviceCountry[firstKey as LanguageOptions] : '';
        };

        const handleTerms22KwAndScReplacement = () => {
          const replacement = config.TERMS_22_KW_AND_SC_REPLACEMENT;
          if (replacement == null) {
            return null;
          }

          // Only one language
          if (typeof replacement === 'string') {
            return replacement;
          }

          // Multiple languages
          if (selectedLanguage && replacement[selectedLanguage]) {
            return replacement[selectedLanguage];
          }

          return null;
        };

        const handlePriceReplacement = () => {
          const price = config.SERVICE_PRICE;
          if (price == null) {
            return null;
          }

          // Only one language
          if (typeof price === 'string') {
            return price;
          }

          // Multiple languages
          if (selectedLanguage && price[selectedLanguage]) {
            return price[selectedLanguage];
          }

          return null;
        };

        if (fileContents) {
          const termsReplacement = handleTerms22KwAndScReplacement();
          const fileContentsWithCustom22 =
            termsReplacement !== null
              ? fileContents.replace(
                  /(^###\s*2\.2[^\n]*\n+)([\s\S]*?)(?=\n{2,}|\n###\s|\n##\s|\n#\s|$)/m,
                  (_match: string, sectionTitle: string) =>
                    `${sectionTitle}${termsReplacement}`,
                )
              : fileContents;

          // Use variables from config
          const text = fileContentsWithCustom22
            .replace(/COMPANY_NAME/g, config.COMPANY_NAME)
            .replace(/COMPANY_ADDRESS/g, config.COMPANY_ADDRESS)
            .replace(
              /REGISTRATION_NUMBER/g,
              config.REGISTRATION_NUMBER,
            )
            .replace(/SERVICE_NAME/g, config.SERVICE_NAME)
            .replace(
              /SERVICE_CONTACT_EMAIL/g,
              config.SERVICE_CONTACT_EMAIL,
            )
            .replace(
              /COMPANY_COUNTRY/g,
              COMPANY_COUNTRY_WORDING?.[config.COMPANY_COUNTRY][
                selectedLanguage
              ],
            )
            .replace(/SERVICE_COUNTRY/g, handleServiceCountry())
            .replace(
              /CUSTOMER_CARE_PHONE_NUMBER/g,
              config.CUSTOMER_CARE_PHONE_NUMBER,
            )
            .replace(/KEYWORD/g, config.KEYWORD ?? 'ERROR_KEYWORD')
            .replace(
              /SHORT_CODE/g,
              config.SHORT_CODE ?? 'ERROR_SHORT_CODE',
            )
            .replace(/SERVICE_PRICE/g, handlePriceReplacement())
            .replace(/SERVICE_URL_LINK/g, () => {
              const url = config.SERVICE_URL_LINK;
              return url.startsWith('http') ? url : `https://${url}`;
            });

          setMarkdownContent(text);
        }
      } catch (error) {
        console.error('[Terms] Error loading Markdown file:', error);
        setMarkdownContent('Error loading content.');
      }
    };

    if (config && selectedLanguage && hasLangLoaded) loadMarkdown();
  }, [config, selectedLanguage, wording, hasLangLoaded]);

  return (
    <main className='max-w-[1920px] mx-auto overflow-hidden'>
      {!markdownContent && <Loader />}
      {markdownContent && (
        <div className='min-h-screen sm:p-8 px-4'>
          <div className='max-w-4xl mx-auto py-4'>
            <TermsSpecificToCH />
            <TermsSpecificToCY />
            <TermsSpecificToNO />
            <Markdown
              className='prose prose-lg'
              components={{
                h1: ({ node, ...props }) => (
                  <h1
                    className='text-3xl font-bold my-6'
                    style={{ color: config.theme.textColorDark }}
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    className='text-2xl font-semibold mt-4 mb-2'
                    style={{ color: config.theme.textColorDark }}
                    {...props}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <h3
                    className='text-xl font-semibold mt-4'
                    style={{ color: config.theme.textColorDark }}
                    {...props}
                  />
                ),
                p: ({ node, ...props }) => (
                  <p
                    className='mb-4'
                    style={{ color: config.theme.textColorDark }}
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul
                    className='list-disc pl-6 mb-4'
                    style={{ color: config.theme.textColorDark }}
                    {...props}
                  />
                ),
                a: ({ href, ...props }) => (
                  <a
                    href={href}
                    className='text-blue-600'
                    style={{ color: config.theme.accentColorOne }}
                    {...props}
                  />
                ),
              }}
            >
              {markdownContent}
            </Markdown>
          </div>
        </div>
      )}
    </main>
  );
}
